import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { SafeAreaView } from 'react-native-safe-area-context';

import { theme } from '@garnish/constants';
import { Button, useToggleState, Icon, IconLink, useResponsive } from '@garnish/core';
import { Modal, ModalDialogue, WithTooltip } from '@garnish/overlays';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Info icon that when pressed renders a tooltip on SM+ and a modal dialogue on XS.
 */
export const BagHsaFsaInfoIcon = () => {
  const { currentBreakpoint } = useResponsive();

  const { formatMessage } = useIntl();
  const title = formatMessage(messages.title);
  const message = formatMessage(messages.message);
  const a11yLabel = formatMessage(messages.a11yLabel);
  const closeLabel = formatMessage(messages.closeLabel);

  const {
    value: isShowingModal,
    toggleOn: openModal,
    toggleOff: closeModal,
  } = useToggleState(false);

  // ─────────────────────────────────────────────────────────────────────

  if (currentBreakpoint.isXS) {
    return (
      <>
        <IconLink
          name="IconInfo"
          color={theme.colors.CHARCOAL}
          accessibilityLabel={a11yLabel}
          onPress={openModal}
          width={16}
          height={16}
        />

        <ModalDialogue
          size="medium"
          accessibilityLabel={a11yLabel}
          visible={isShowingModal}
          headline={title}
          body={message}
          onRequestClose={closeModal}
        >
          <SafeAreaView edges={['bottom']}>
            <Modal.Footer withoutTopBorder>
              <Button
                onPress={closeModal}
                palette="primary"
                accessibilityRole="button"
                accessibilityLabel={closeLabel}
                size="large"
              >
                <FormattedMessage {...messages.ctaLabel} />
              </Button>
            </Modal.Footer>
          </SafeAreaView>
        </ModalDialogue>
      </>
    );
  }

  return (
    <WithTooltip text={message} placement="left" triggerMode="press">
      <Icon name="IconInfo" color={theme.colors.CHARCOAL} width={16} height={16} />
    </WithTooltip>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'HSA/FSA eligibility',
    description: 'Bag | HSA/FSA Info Icon | Title',
  },
  message: {
    defaultMessage: `This order doesn't include HSA/FSA-eligible items, so this card can't be used.`,
    description: 'Bag | HSA/FSA Info Icon | Message',
  },
  a11yLabel: {
    defaultMessage: 'HSA/FSA Info',
    description: 'Bag | HSA/FSA Info Icon | A11y',
  },
  closeLabel: {
    defaultMessage: 'Close',
    description: 'Bag | HSA/FSA Info Icon | Close',
  },
  ctaLabel: {
    defaultMessage: 'Got it',
    description: 'Bag | HSA/FSA Info Icon | CTA',
  },
});
